import React, { useEffect, useRef, useState, useMemo } from 'react';
import Modal from 'react-modal';
import axios from 'axios';
import useAuth from '../hooks/useAuth';
import ChannelSocket from '../components/channel-socket';
import '../css/game.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRecycle, faPlay, faGears, faSpinner, faUpload, faDownload, faCopy } from '@fortawesome/free-solid-svg-icons';
import EntriesList from '../components/entries-list';
import ResultSpinner from '../components/games/result-spinner';
import PieWheel from '../components/games/pie-wheel'; // New PieWheel animation component

// Set the app element for accessibility
Modal.setAppElement('#app');

const HIDDEN_FIELDS = [
  'id',
  'guild_id',
  'created_by',
  'created_at',
  'modified_by',
  'modified_at'
];

const keywordStyleMapping = {
  '=': 'eq',
  '<=': 'lte',
  '>=': 'gte',
  '<>': 'ne',
  'contains': 'contains'
};

const reverseKeywordStyleMapping = {
  eq: '=',
  lte: '<=',
  gte: '>=',
  ne: '<>',
  contains: 'contains'
};

const GAME_TYPE_OPTIONS = [
  { value: 'pie-wheel', label: 'Basic Wheel - WIP' },
  { value: 'raffle-basic', label: 'Basic Raffle' }
];

const KEYWORD_STYLE_OPTIONS = ['='];

function Game() {
  const { user, discordServers } = useAuth();
  const [guildData, setGuildData] = useState(null);
  const [guildManagerData, setGuildManagerData] = useState(null);
  const [guildGameData, setGuildGameData] = useState(null);
  const [guildChannelsData, setGuildChannelsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gameSettings, setGameSettings] = useState({});
  const [entries, setEntries] = useState([]);
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [winners, setWinners] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  // Winner modal state and countdown
  const [winnerModalVisible, setWinnerModalVisible] = useState(false);
  const [currentWinner, setCurrentWinner] = useState(null);
  const [winnerCountdown, setWinnerCountdown] = useState(60);
  const [autoRemoveWinner, setAutoRemoveWinner] = useState(false);

  // --- NEW: Winner Chat States ---
  const [winnerChatMessages, setWinnerChatMessages] = useState([]);
  const [winnerChatVisible, setWinnerChatVisible] = useState(true);
  const [autoScroll, setAutoScroll] = useState(true);
  const winnerChatContainerRef = useRef(null);

  // Clear winner chat when a new winner is set
  useEffect(() => {
    setWinnerChatMessages([]);
  }, [currentWinner]);

  // Update document title based on game name and guild name
  const originalTitle = useRef(document.title);
  useEffect(() => {
    if (gameSettings.game_name && guildData && guildData.name) {
      document.title = `${originalTitle.current} x ${guildData.name} - ${gameSettings.game_name}`;
    } else {
      document.title = originalTitle.current;
    }
  }, [gameSettings.game_name, guildData]);

  // Ref to capture latest autoRemoveWinner value
  const autoRemoveWinnerRef = useRef(autoRemoveWinner);
  useEffect(() => {
    autoRemoveWinnerRef.current = autoRemoveWinner;
  }, [autoRemoveWinner]);

  // Spinner state for game animation and waiting modal flag
  const [showResultSpinner, setShowResultSpinner] = useState(false);
  const [spinnerEntries, setSpinnerEntries] = useState([]);
  const [spinnerWinner, setSpinnerWinner] = useState('');
  const [waitingForGameResult, setWaitingForGameResult] = useState(false);

  // State for manual import modal
  const [importText, setImportText] = useState('');
  const [showImportModal, setShowImportModal] = useState(false);

  // Error state
  const [errorMessage, setErrorMessage] = useState(null);

  // Local state for game type changes and editing flag
  const [localGameType, setLocalGameType] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // Send an api call to notify winner in chat when winner modal becomes visible
  useEffect(() => {
    console.info('Winner modal visibility changed:', winnerModalVisible);
    if (winnerModalVisible && currentWinner && currentWinner.winner) {
      console.info('Current winner:', currentWinner);
      console.info('Current entries:', currentWinner.entries);

      // Convert entries object to an array
      const entriesArray = Object.values(currentWinner.entries || {});
      if (entriesArray.length > 0) {
        entriesArray.forEach((entry) => {
          if (entry && entry.entryData && entry.entryData.chatter) {
            const chatterUsername = entry.entryData.chatter.username;
            console.info(`Does ${chatterUsername} match ${currentWinner.winner.username}?`);
            if (
              chatterUsername === currentWinner.winner.username &&
              entry.entryData.platform !== 'manual'
            ) {
              console.info('Winner entry found:', entry);
              const platform = entry.entryData.platform;
              const channel = entry.entryData.channel;
              const winnerName = currentWinner.winner.username;
              const message = `Congratulations, @${winnerName}! Respond in chat before the countdown expires to not be re-rolled.`;
              axios.post(`${API_BASE_URL}:${PORT}/api/chat/${platform}/${channel}`, { message })
                .then(res => {
                  console.log('Winner notification sent:', res.data);
                })
                .catch(err => {
                  console.error('Error sending winner notification:', err);
                });
            }
          } else {
            console.warn('Entry missing expected structure:', entry);
          }
        });
      } else {
        console.warn('No entries found in currentWinner.entries');
      }
    }
  }, [winnerModalVisible, currentWinner]);

  // Scroll handling for winner chat auto-scroll
  const handleWinnerChatScroll = () => {
    if (!winnerChatContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = winnerChatContainerRef.current;
    // If within 20px of bottom, enable auto scroll; otherwise, pause auto-scroll.
    if (scrollTop + clientHeight >= scrollHeight - 20) {
      setAutoScroll(true);
    } else {
      setAutoScroll(false);
    }
  };

  // Auto-scroll the winner chat container when new messages arrive (if enabled)
  useEffect(() => {
    if (autoScroll && winnerChatContainerRef.current) {
      winnerChatContainerRef.current.scrollTop = winnerChatContainerRef.current.scrollHeight;
    }
  }, [winnerChatMessages, autoScroll]);

  // Export winner chat messages to clipboard in "Username: Message" format
  const handleWinnerChatExport = () => {
    const text = winnerChatMessages
      .map(msg => `${msg.chatter.username}: ${msg.message.content}`)
      .join('\n');
    navigator.clipboard.writeText(text)
      .catch(err => console.error('Error exporting winner chat:', err));
  };

  // Refs to hold latest gameSettings and localGameType
  const gameSettingsRef = useRef(gameSettings);
  useEffect(() => {
    gameSettingsRef.current = gameSettings;
  }, [gameSettings]);

  const localGameTypeRef = useRef(localGameType);
  useEffect(() => {
    localGameTypeRef.current = localGameType;
  }, [localGameType]);

  const API_BASE_URL =
    process.env.REACT_APP_ENVIRONMENT === 'production'
      ? process.env.REACT_APP_PRODUCTION_API_BASE_URL
      : process.env.REACT_APP_DEVELOPMENT_API_BASE_URL;
  const PORT =
    process.env.REACT_APP_ENVIRONMENT === 'production'
      ? process.env.REACT_APP_PRODUCTION_PORT
      : process.env.REACT_APP_DEVELOPMENT_PORT;

  const botwinzEnabled =
    guildData?.botwinz_enabled === true || guildData?.botwinz_enabled === 'true';

  const gameId = window.location.pathname.split('/')[4];
  const guildIdFromPath = window.location.pathname.split('/')[2];
  let activeServer = {};
  let isAuthorized = false;
  for (const server of discordServers) {
    if (server.id === guildIdFromPath) {
      isAuthorized = true;
      activeServer = server;
      break;
    }
  }
  if (!user) {
    window.location.href = '/';
    return null;
  }

  useEffect(() => {
    if (winnerModalVisible) {
      const cheerAudio = new Audio('/sound/cheering.mp3');
      cheerAudio.play().catch(error => {
        console.error('Cheering audio playback failed:', error);
      });
    }
  }, [winnerModalVisible]);

  useEffect(() => {
    if (!isAuthorized) {
      setErrorMessage('Unauthorized access.');
    }
  }, [isAuthorized]);

  const hasEditPermission = () => {
    if (!guildManagerData || !guildManagerData[user.id]) return false;
    const perms = guildManagerData[user.id];
    return perms.admin === 'true' || perms.games_admin === 'true' || perms.games_edit === 'true';
  };

  const hasViewPermission = () => {
    if (!guildManagerData || !guildManagerData[user.id]) return false;
    const perms = guildManagerData[user.id];
    return perms.admin === 'true' || perms.games_admin === 'true' || perms.games_view === 'true';
  };

  useEffect(() => {
    if (guildManagerData && !hasViewPermission()) {
      setErrorMessage('403 Forbidden: You do not have permission to view this page.');
    }
  }, [guildManagerData, user.id]);

  const memoizedChannelSockets = useMemo(() => {
    if (!guildChannelsData) return null;
    return (
      <div style={{ display: 'none' }}>
        {Object.values(guildChannelsData)
          .sort((a, b) => a.id.localeCompare(b.id))
          .map(channel => {
            const wsUrl = `${channel.platform}_${channel.id}_chat`;
            return (
              <ChannelSocket
                key={channel.id}
                wsUrl={wsUrl}
                onMessage={(message) => {
                  console.log('Received message on channel', channel.id, message);
                  if (currentWinner && currentWinner.winner) {
                    if (message.chatter && message.chatter.username === currentWinner.winner.username) {
                      console.log('Appending winner chat message:', message);
                      setWinnerChatMessages(prev => [...prev, message]);
                      if (winnerModalVisible) {
                        handleWinnerRespond();
                      }
                    }
                  } else {
                    console.warn('No current winner set, skipping appending chat message.');
                  }
                }}
              />
            );
          })}
      </div>
    );
  }, [guildChannelsData, winnerModalVisible, currentWinner]);

  const entriesSelectRef = useRef(null);
  const winnerTimerRef = useRef(null);
  const pollIntervalRef = useRef(null);

  // Helper: compute the entry ID (used for winner removal)
  const computeEntryId = () => {
    if (!currentWinner || !currentWinner.result) return '';
    return currentWinner.result.winner;
  };

  // COPY/EXPORT HANDLERS for entries and winners
  const handleEntriesExport = () => {
    const lines = entries.map(entry => entry.chatter?.username || 'Unknown');
    if (!lines.length) {
      alert('No entries to export.');
      return;
    }
    const text = lines.join('\n');
    navigator.clipboard.writeText(text)
      .catch(err => console.error('Failed to copy entries:', err));
  };

  const handleWinnersExport = () => {
    const lines = winners.map(w => w.winner?.username || 'Unknown');
    if (!lines.length) {
      alert('No winners to export.');
      return;
    }
    const text = lines.join('\n');
    navigator.clipboard.writeText(text)
      .catch(err => console.error('Failed to copy winners:', err));
  };

  // Handler for importing manual entries
  const handleImportEntries = async () => {
    const names = importText.split('\n').map(name => name.trim()).filter(name => name);
    if (!names.length) {
      alert('No valid names found.');
      return;
    }
    const importPromises = names.map((name, index) => {
      const slug = name.toLowerCase().replace(/\s+/g, '-');
      const entryId = `manual-${slug}`;
      return axios.put(`${API_BASE_URL}:${PORT}/api/games/${activeServer.id}/game/${gameId}/${entryId}`, {
        name,
        slug
      });
    });
    try {
      await Promise.all(importPromises);
      setShowImportModal(false);
      setImportText('');
      fetchGameData();
    } catch (error) {
      console.error('Error importing entries', error);
    }
  };

  // Helper: fetch game data from the server and update state.
  const fetchGameData = () => {
    axios.get(`${API_BASE_URL}:${PORT}/api/games/${activeServer.id}/game/${gameId}`)
      .then(({ data }) => {
        const { entries, winners, ...settings } = data;
        setEntries(entries ? Object.values(entries) : []);
        setWinners(winners || []);
        const newSettings = { ...gameSettingsRef.current };
        for (const key in settings) {
          if (!HIDDEN_FIELDS.includes(key) && key !== 'game_type') {
            newSettings[key] = key === 'keyword_style'
              ? reverseKeywordStyleMapping[settings[key]] || settings[key]
              : settings[key];
          }
        }
        newSettings.game_type = localGameTypeRef.current !== null
          ? localGameTypeRef.current
          : settings.game_type;
        if (!newSettings.keyword_countdown_timer) {
          newSettings.keyword_countdown_timer = '60';
        }
        if (newSettings.remove_winner_from_entries === undefined) {
          newSettings.remove_winner_from_entries = 'false';
        }
        setAutoRemoveWinner(newSettings.remove_winner_from_entries === 'true');
        setGameSettings(newSettings);
      })
      .catch(err => console.error('Error fetching guild game:', err));
  };

  // Poll data every 5 seconds
  useEffect(() => {
    if (!activeServer.id) {
      setLoading(false);
      return;
    }
    const pollAllData = () => {
      if (winnerModalVisible || showResultSpinner || isEditing) return;
      Promise.all([
        axios.get(`${API_BASE_URL}:${PORT}/api/guild/${activeServer.id}`)
          .then(({ data }) => setGuildData(data))
          .catch(err => console.error('Error fetching guild data:', err)),
        axios.get(`${API_BASE_URL}:${PORT}/api/guild/${activeServer.id}/managers`)
          .then(({ data }) => setGuildManagerData(data))
          .catch(err => console.error('Error fetching guild managers:', err)),
        axios.get(`${API_BASE_URL}:${PORT}/api/guild/${activeServer.id}/channels`)
          .then(({ data }) => {
            const newChannelIds = Object.values(data)
              .map(channel => channel.id)
              .sort()
              .join(',');
            const currentChannelIds = guildChannelsData
              ? Object.values(guildChannelsData).map(c => c.id).sort().join(',')
              : '';
            if (newChannelIds !== currentChannelIds) {
              setGuildChannelsData(data);
            }
          })
          .catch(err => console.error('Error fetching guild channels:', err)),
        axios.get(`${API_BASE_URL}:${PORT}/api/games/${activeServer.id}/game/${gameId}`)
          .then(({ data }) => {
            const { entries, winners, ...settings } = data;
            setEntries(entries ? Object.values(entries) : []);
            setWinners(winners || []);
            const newSettings = { ...gameSettingsRef.current };
            for (const key in settings) {
              if (!HIDDEN_FIELDS.includes(key) && key !== 'game_type') {
                newSettings[key] = key === 'keyword_style'
                  ? reverseKeywordStyleMapping[settings[key]] || settings[key]
                  : settings[key];
              }
            }
            newSettings.game_type = localGameTypeRef.current !== null
              ? localGameTypeRef.current
              : settings.game_type;
            if (!newSettings.keyword_countdown_timer) {
              newSettings.keyword_countdown_timer = '60';
            }
            if (newSettings.remove_winner_from_entries === undefined) {
              newSettings.remove_winner_from_entries = 'false';
            }
            setAutoRemoveWinner(newSettings.remove_winner_from_entries === 'true');
            setGameSettings(newSettings);
          })
          .catch(err => console.error('Error fetching guild game:', err))
      ]).finally(() => setLoading(false));
    };

    pollAllData();
    pollIntervalRef.current = setInterval(pollAllData, 5000);
    return () => clearInterval(pollIntervalRef.current);
  }, [activeServer.id, API_BASE_URL, PORT, gameId, winnerModalVisible, showResultSpinner, isEditing]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (entriesSelectRef.current && !entriesSelectRef.current.contains(e.target)) {
        setSelectedEntries([]);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Start winner countdown when modal is visible.
  useEffect(() => {
    if (!winnerModalVisible) return;
    setWinnerCountdown(parseInt(gameSettings.keyword_countdown_timer) || 60);
    winnerTimerRef.current = setInterval(() => {
      setWinnerCountdown(prev => {
        if (prev <= 1) {
          clearInterval(winnerTimerRef.current);
          handleCountdownExpire();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(winnerTimerRef.current);
  }, [winnerModalVisible]);

  if (errorMessage) return <div>{errorMessage}</div>;
  if (loading) return <div>Loading...</div>;

  // Select animation component based on game type.
  let AnimationComponent;
  switch (gameSettings.game_type) {
    case 'pie-wheel':
      AnimationComponent = PieWheel;
      break;
    default:
      AnimationComponent = ResultSpinner;
  }

  // =========== Handlers ===========
  const handleUpdateSettings = (updatedSettings = gameSettings) => {
    const transformedSettings = {
      ...updatedSettings,
      keyword_style: keywordStyleMapping[updatedSettings.keyword_style] || updatedSettings.keyword_style
    };
    axios.put(`${API_BASE_URL}:${PORT}/api/games/${activeServer.id}/game/${gameId}`, transformedSettings)
      .then(res => {
        console.debug('Game settings updated', res.data);
        const { entries, winners, ...updatedFromServer } = res.data;
        const filtered = Object.keys(updatedFromServer).reduce((acc, key) => {
          if (!HIDDEN_FIELDS.includes(key)) {
            acc[key] = key === 'keyword_style'
              ? reverseKeywordStyleMapping[updatedFromServer[key]] || updatedFromServer[key]
              : updatedFromServer[key];
          }
          return acc;
        }, {});
        filtered.game_type = updatedSettings.game_type;
        setGameSettings(filtered);
        fetchGameData();
      })
      .catch(err => console.error('Error updating game settings', err));
  };

  const handleStartGame = () => {
    if (!hasEditPermission()) return;
    setWaitingForGameResult(true);
    axios.post(`${API_BASE_URL}:${PORT}/api/games/${activeServer.id}/game/${gameId}/result`)
      .then(response => {
        setWaitingForGameResult(false);
        if (response.data.winnerObject) {
          const allEntryObjects = Object.values(response.data.resultData.entries);
          const spinnerEntries = allEntryObjects.map(entry => ({
            username: entry.entryData.chatter.username,
            avatar: entry.entryData.chatter.avatar
          }));
          setSpinnerEntries(spinnerEntries);
          setSpinnerWinner(response.data.winnerObject.winner);
          setCurrentWinner(response.data.winnerObject);
          setShowResultSpinner(true);
        } else if (response.data.winner) {
          console.error('API did not return winnerObject.');
        } else {
          setWinners([]);
        }
      })
      .catch(error => {
        console.error('Error starting game', error);
        setWaitingForGameResult(false);
      });
  };

  const handleSettingChange = (key, value) => {
    if (key === 'keyword_style') {
      if (!KEYWORD_STYLE_OPTIONS.includes(value)) {
        console.error(`Invalid keyword_style value: ${value}`);
        return;
      }
      value = keywordStyleMapping[value] || value;
    }
    if (key === 'game_type') {
      setLocalGameType(value);
      localGameTypeRef.current = value;
    }
    setGameSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleClearEntries = () => {
    if (!hasEditPermission()) return;
    axios.delete(`${API_BASE_URL}:${PORT}/api/games/${activeServer.id}/game/${gameId}/all`)
      .then(res => {
        console.log('Cleared all entries:', res.data);
        setEntries([]);
        setWinners([]);
      })
      .catch(err => console.error('Error clearing entries:', err));
  };

  const handleClearConfirm = () => {
    handleClearEntries();
    setShowClearConfirm(false);
  };

  const handleClearCancel = () => {
    setShowClearConfirm(false);
  };

  const upsertLocalWinner = updatedObj => {
    setWinners(prev => {
      const filtered = prev.filter(w => w.round_id !== updatedObj.round_id);
      return [updatedObj, ...filtered];
    });
  };

  const handleWinnerRespond = () => {
    if (autoRemoveWinnerRef.current && currentWinner?.winner) {
      const entryId = computeEntryId();
      axios.delete(`${API_BASE_URL}:${PORT}/api/games/${activeServer.id}/game/${gameId}/${entryId}`)
        .then(() => {
          setEntries(prev => prev.filter(entry => entry.id !== entryId));
        })
        .catch(error => console.error('Error removing winner entry:', error));
    }
    axios.put(`${API_BASE_URL}:${PORT}/api/games/${activeServer.id}/game/${gameId}/winner/${currentWinner.round_id}`, {
      winner_announced: 'true'
    })
      .then(() => {
        const updatedWinnerLocal = { ...currentWinner, winner_announced: 'true' };
        upsertLocalWinner(updatedWinnerLocal);
      })
      .catch(err => console.error('Error updating winner record', err));
    clearInterval(winnerTimerRef.current);
    setWinnerModalVisible(false);
  };

  const handleCountdownExpire = () => {
    if (!currentWinner) return;
    if (autoRemoveWinnerRef.current && currentWinner?.winner) {
      const entryId = computeEntryId();
      axios.delete(`${API_BASE_URL}:${PORT}/api/games/${activeServer.id}/game/${gameId}/${entryId}`)
        .then(() => {
          setEntries(prev => prev.filter(entry => entry.id !== entryId));
        })
        .catch(error => console.error('Error removing winner entry:', error));
    }
    let newUsername = currentWinner.winner.username;
    const updatedWinnerLocal = {
      ...currentWinner,
      winner: { ...currentWinner.winner, rolled_over: 'true', username: newUsername },
      winner_announced: 'true',
      re_rolled: 'true'
    };
    axios.put(`${API_BASE_URL}:${PORT}/api/games/${activeServer.id}/game/${gameId}/winner/${currentWinner.round_id}`, {
      winner_announced: 'true',
      re_rolled: 'true'
    })
      .then(() => {
        upsertLocalWinner(updatedWinnerLocal);
      })
      .catch(err => console.error('Error updating winner record', err));
    clearInterval(winnerTimerRef.current);
    setWinnerModalVisible(false);
  };

  const handleSkip = () => {
    if (!currentWinner) return;
    if (autoRemoveWinnerRef.current && currentWinner?.winner) {
      const entryId = computeEntryId();
      axios.delete(`${API_BASE_URL}:${PORT}/api/games/${activeServer.id}/game/${gameId}/${entryId}`)
        .then(() => {
          setEntries(prev => prev.filter(entry => entry.id !== entryId));
        })
        .catch(error => console.error('Error removing winner entry:', error));
    }
    let newUsername = currentWinner.winner.username;
    const updatedWinnerLocal = {
      ...currentWinner,
      winner: { ...currentWinner.winner, rolled_over: 'true', username: newUsername },
      winner_announced: 'true',
      re_rolled: 'false',
      skipped: 'true'
    };
    axios.put(`${API_BASE_URL}:${PORT}/api/games/${activeServer.id}/game/${gameId}/winner/${currentWinner.round_id}`, {
      winner_announced: 'true',
      re_rolled: 'false',
      skipped: 'true'
    })
      .then(() => {
        upsertLocalWinner(updatedWinnerLocal);
      })
      .catch(err => console.error('Error updating winner record', err));
    clearInterval(winnerTimerRef.current);
    setWinnerModalVisible(false);
  };

  const renderKeywordStyleField = (key, labelOverride) => {
    const value = gameSettings[key];
    const label = labelOverride || key;
    return (
      <label key={key} className="setting-label">
        {label && <span>{label}:</span>}
        <select
          disabled={!hasEditPermission()}
          value={value}
          onChange={e => handleSettingChange(key, e.target.value)}
          onFocus={() => setIsEditing(true)}
          onBlur={() => {
            setIsEditing(false);
            handleUpdateSettings();
          }}
        >
          {KEYWORD_STYLE_OPTIONS.map(opt => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </label>
    );
  };

  const renderSettingFieldExtended = (key, labelOverride, noLabel = false) => {
    const label = labelOverride || key;
    const value = gameSettings[key];
    if (key === 'keyword_style') {
      return renderKeywordStyleField(key, label);
    }
    if (value === 'true' || value === 'false') {
      return (
        <label key={key} className="setting-label boolean-toggle">
          {noLabel === false ? <span>{label}:</span> : ''}
          <div className="toggle-switch">
            <input
              type="checkbox"
              disabled={!hasEditPermission()}
              checked={value === 'true'}
              onChange={() => {
                const newVal = value === 'true' ? 'false' : 'true';
                handleSettingChange(key, newVal);
              }}
              onFocus={() => setIsEditing(true)}
              onBlur={() => {
                setIsEditing(false);
                handleUpdateSettings();
              }}
            />
            <span className="slider" />
          </div>
        </label>
      );
    }
    if (key === 'game_type') {
      return (
        <label key={key} className="setting-label">
          <span>{label}:</span>
          <select
            disabled={!hasEditPermission()}
            value={value}
            onChange={e => {
              handleSettingChange(key, e.target.value);
              handleUpdateSettings({ ...gameSettingsRef.current, game_type: e.target.value });
            }}
            onFocus={() => setIsEditing(true)}
            onBlur={() => {
              setIsEditing(false);
              handleUpdateSettings();
            }}
          >
            {GAME_TYPE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
      );
    }
    if (key === 'keyword_countdown_timer' || key === 'keyword_require_points') {
      return (
        <label key={key} className="setting-label">
          <span>{label}:</span>
          <input
            type="number"
            disabled={!hasEditPermission()}
            value={value}
            onChange={e => handleSettingChange(key, e.target.value)}
            onFocus={() => setIsEditing(true)}
            onBlur={() => {
              setIsEditing(false);
              handleUpdateSettings();
            }}
          />
        </label>
      );
    }
    return (
      <label key={key} className="setting-label">
        <span>{label}:</span>
        <input
          type="text"
          disabled={!hasEditPermission()}
          value={value || ''}
          onChange={e => handleSettingChange(key, e.target.value)}
          onFocus={() => setIsEditing(true)}
          onBlur={() => {
            setIsEditing(false);
            handleUpdateSettings();
          }}
        />
      </label>
    );
  };

  const stripKeywordPrefix = key => {
    let stripped = '';
    if (key.startsWith('keyword_require_botwinz_')) {
      stripped = key.replace('keyword_require_botwinz_', '');
    } else {
      stripped = key.replace('keyword_require_', '');
    }
    // Replace underscores with spaces and title-case each word
    stripped = stripped.replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    return stripped;
  };

  const handleEntriesSelect = e => {
    const selected = Array.from(e.target.selectedOptions).map(option => option.value);
    setSelectedEntries(selected);
  };

  return (
    <div className="page-container">
      {memoizedChannelSockets}
      <div className="top-row">
        {/* Entries Column */}
        <div className="column">
          <div className="column-header">
            <h2>Entries ({entries.length})</h2>
            <button onClick={handleEntriesExport}>
              <FontAwesomeIcon icon={faDownload} />
            </button>
          </div>
          <EntriesList
            entries={entries}
            onRemoveEntries={entriesToRemove => {
              setEntries(prev => {
                const removeIds = entriesToRemove.map(e => e.id);
                return prev.filter(entry => !removeIds.includes(entry.id));
              });
            }}
            API_BASE_URL={API_BASE_URL}
            PORT={PORT}
            activeServer={activeServer}
            gameId={gameId}
          />
        </div>

        {/* Quick Actions Column */}
        <div className="column">
          <h2>Quick Actions</h2>
          <div className="keyword-line">
            {renderKeywordStyleField('keyword_style', 'Keyword')}
            <label className="setting-label keyword-input">
              <input
                type="text"
                disabled={!hasEditPermission()}
                value={gameSettings.keyword || ''}
                onChange={e => handleSettingChange('keyword', e.target.value)}
                onFocus={() => setIsEditing(true)}
                onBlur={() => {
                  setIsEditing(false);
                  handleUpdateSettings();
                }}
              />
            </label>
            {renderSettingFieldExtended('keyword_enter', '', true)}
          </div>
          <div className="quick-actions-buttons">
            <button
              className="start-game-button"
              onClick={hasEditPermission() ? handleStartGame : null}
              disabled={!hasEditPermission()}
            >
              <FontAwesomeIcon icon={faPlay} /> Start
            </button>
            <button
              className="start-game-button"
              onClick={hasEditPermission() ? () => setShowClearConfirm(true) : null}
              disabled={!hasEditPermission()}
            >
              <FontAwesomeIcon icon={faRecycle} /> Clear
            </button>
            <button
              className="start-game-button"
              onClick={hasEditPermission() ? () => setShowImportModal(true) : null}
              disabled={!hasEditPermission()}
            >
              <FontAwesomeIcon icon={faUpload} /> Import
            </button>
            <button
              className="start-game-button"
              onClick={() => setShowSettings(!showSettings)}
            >
              <FontAwesomeIcon icon={faGears} /> {showSettings ? 'Hide' : 'Show'}
            </button>
          </div>
        </div>

        {/* Winners Column */}
        <div className="column">
          <div className="column-header">
            <h2>Winners ({winners.length})</h2>
            <button onClick={handleWinnersExport}>
              <FontAwesomeIcon icon={faDownload} />
            </button>
          </div>
          <select multiple size={8} className="entries-multiselect" disabled>
            {winners.length === 0 ? (
              <option>No winners yet</option>
            ) : (
              winners.map((winnerObj, index) => {
                if (winnerObj.winner_announced !== 'true') return null;

                const winnerKey = winnerObj.result?.winner;
                const winnerEntry = winnerKey ? winnerObj.entries?.[winnerKey] : null;
                const displayName = winnerEntry?.entryData?.name || 'unknown';

                return (
                  <option key={index} value={displayName}>
                    {displayName}
                    {winnerObj.re_rolled === 'true' ? ' (re-rolled)' : ''}
                    {winnerObj.skipped === 'true' ? ' (skipped)' : ''}
                  </option>
                );
              })
            )}
          </select>
          {/* NEW: Winner Chat Section */}
          <div className="winner-chat-section">
            <div className="winner-chat-header" onClick={() => setWinnerChatVisible(!winnerChatVisible)}>
              <h3>Winner Chat {winnerChatVisible ? '▼' : '▲'}</h3>
              <button
                onClick={(e) => {
                  e.stopPropagation(); // Prevents the toggle from triggering
                  handleWinnerChatExport();
                }}
              >
                <FontAwesomeIcon icon={faDownload} />
              </button>
              {!autoScroll && <div className="auto-scroll-paused">Auto-scroll paused</div>}
            </div>
            {winnerChatVisible && (
              <div
                className="winner-chat-container"
                ref={winnerChatContainerRef}
                onScroll={handleWinnerChatScroll}
              >
                {winnerChatMessages.map((msg, index) => (
                  <div key={index} className="winner-chat-message">
                    <span>
                      <strong>{msg.chatter.username}:</strong> {msg.message.content}
                    </span>
                    <FontAwesomeIcon
                      icon={faCopy}
                      className="copy-icon"
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent the header toggle if inside a clickable container
                        navigator.clipboard.writeText(msg.message.content)
                          .then(() => console.log("Copied message to clipboard"))
                          .catch(err => console.error("Copy failed", err));
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Settings Section */}
      {showSettings && (
        <div className="settings-row">
          <div className="settings-column">
            <h3>Main Settings</h3>
            {renderSettingFieldExtended('active', 'Game Is Active')}
            {renderSettingFieldExtended('game_name', 'Game Name')}
            {renderSettingFieldExtended('game_type', 'Game Type')}
            {renderSettingFieldExtended('keyword_countdown_timer', 'Countdown (seconds)')}
            {renderSettingFieldExtended('remove_winner_from_entries', 'Automatically remove winner')}
          </div>
          <div className="settings-column">
            <h3>Keyword Requirements</h3>
            <div className="keyword-settings-top">
              {renderKeywordStyleField('keyword_style', 'Keyword Type')}
              {renderSettingFieldExtended('keyword', 'Keyword')}
              {renderSettingFieldExtended('keyword_enter', 'Keyword Entry Active')}
            </div>
            {Object.keys(gameSettings)
              .filter(
                k =>
                  k.startsWith('keyword_require_') &&
                  !k.startsWith('keyword_require_botwinz_')
              )
              .map(fieldKey =>
                renderSettingFieldExtended(fieldKey, stripKeywordPrefix(fieldKey))
              )}
          </div>
          {botwinzEnabled && (
            <div className="settings-column">
              <h3>BotWinz Integration</h3>
              {Object.keys(gameSettings)
                .filter(k => k.startsWith('keyword_require_botwinz_'))
                .map(fieldKey =>
                  renderSettingFieldExtended(fieldKey, stripKeywordPrefix(fieldKey))
                )}
            </div>
          )}
        </div>
      )}

      {/* Waiting for game result modal */}
      <Modal
        isOpen={waitingForGameResult}
        overlayClassName="modal-overlay"
        className="modal-content"
        shouldCloseOnOverlayClick={false}
      >
        <div className="modal-center">
          <h2>
            <FontAwesomeIcon icon={faSpinner} spin /> Waiting for EOS Block...
          </h2>
        </div>
      </Modal>

      {/* Game Animation Modal */}
      <Modal
        isOpen={showResultSpinner}
        overlayClassName="modal-overlay"
        className="modal-content modal-game-content"
        shouldCloseOnOverlayClick={false}
      >
        {(() => {
          let Animation;
          switch (gameSettings.game_type) {
            case 'pie-wheel':
              Animation = PieWheel;
              break;
            default:
              Animation = ResultSpinner;
          }
          return (
            <Animation
              entries={spinnerEntries}
              winner={spinnerWinner}
              onComplete={() => {
                setShowResultSpinner(false);
                setWinnerModalVisible(true);
              }}
              duration={7000}
              numRotations={3}
              startPause={2000}
              finalPause={2000}
            />
          );
        })()}
      </Modal>

      {/* Clear Entries confirmation modal */}
      <Modal
        isOpen={showClearConfirm}
        onRequestClose={handleClearCancel}
        overlayClassName="modal-overlay"
        className="modal-content"
        shouldCloseOnOverlayClick={true}
      >
        <div>
          <p>Are you sure you want to clear all entries from the game?</p>
          <span className="modal-buttons">
            <button className="modal-button" onClick={handleClearConfirm}>
              Yes
            </button>
            <button className="modal-button" onClick={handleClearCancel}>
              No
            </button>
          </span>
        </div>
      </Modal>

      {/* Import Manual Entries Modal */}
      <Modal
        isOpen={showImportModal}
        onRequestClose={() => setShowImportModal(false)}
        overlayClassName="modal-overlay"
        className="modal-content"
        shouldCloseOnOverlayClick={true}
      >
        <div>
          <h2>Import Manual Entries</h2>
          <p>Enter one entrant name per line:</p>
          <textarea
            className="import-textarea"
            value={importText}
            onChange={e => setImportText(e.target.value)}
          />
          <div className="import-modal-actions">
            <button className="modal-button" onClick={() => setShowImportModal(false)}>
              Cancel
            </button>
            <button className="modal-button" onClick={handleImportEntries}>
              Import
            </button>
          </div>
        </div>
      </Modal>

      {/* Winner Modal */}
      <Modal
        isOpen={winnerModalVisible}
        onRequestClose={() => setWinnerModalVisible(false)}
        overlayClassName="modal-overlay"
        className="modal-content"
        shouldCloseOnOverlayClick={false}
      >
        {currentWinner && currentWinner.winner && (
          <div className="winner-modal">
            <div className="winner-info">
              <img
                src={currentWinner.winner.avatar}
                alt={currentWinner.winner.username}
                className="winner-avatar"
              />
              <h1 className="winner-name">
                {currentWinner.winner.username}
                {currentWinner.re_rolled === "true" ? " (re-rolled)" : ""}
                {currentWinner.skipped === "true" ? " (skipped)" : ""}
              </h1>
            </div>
            <p className="winner-countdown">
              Respond within: {winnerCountdown} seconds
            </p>
            <div className="winner-buttons">
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={autoRemoveWinner}
                  onChange={() => {
                    setAutoRemoveWinner(!autoRemoveWinner);
                    handleSettingChange(
                      "remove_winner_from_entries",
                      !autoRemoveWinner ? "true" : "false"
                    );
                    handleUpdateSettings({
                      ...gameSettings,
                      remove_winner_from_entries: !autoRemoveWinner ? "true" : "false",
                    });
                  }}
                />
                &nbsp;Remove Winner
              </label>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              <button className="modal-button" onClick={handleSkip}>
                Skip
              </button>
              {/* Optionally add a Respond button here */}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default Game;
